import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Zap, Target, ClipboardCheck, Trophy, BoxIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { ModeMetadata } from '../../modes/types';
interface ModeCardProps {
  mode: ModeMetadata;
  onClick: () => void;
}
const iconMap = {
  Zap,
  Target,
  ClipboardCheck,
  Trophy,
  BoxIcon
};
const colorMap: Record<string, {
  bg: string;
  text: string;
  border: string;
}> = {
  Zap: {
    bg: 'bg-amber-50',
    text: 'text-amber-600',
    border: 'border-amber-200'
  },
  Target: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-600',
    border: 'border-indigo-200'
  },
  ClipboardCheck: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-600',
    border: 'border-emerald-200'
  },
  Trophy: {
    bg: 'bg-violet-50',
    text: 'text-violet-600',
    border: 'border-violet-200'
  },
  BoxIcon: {
    bg: 'bg-slate-50',
    text: 'text-slate-600',
    border: 'border-slate-200'
  }
};
export function ModeCard({
  mode,
  onClick
}: ModeCardProps) {
  const Icon = iconMap[mode.icon as keyof typeof iconMap] || BoxIcon;
  const colors = colorMap[mode.icon] || colorMap.BoxIcon;
  const isDisabled = !mode.isEnabled;
  return <motion.div whileHover={!isDisabled ? {
    y: -4,
    scale: 1.02
  } : {}} whileTap={!isDisabled ? {
    scale: 0.98
  } : {}} transition={{
    type: 'spring',
    stiffness: 400,
    damping: 25
  }} className="h-full" data-id="element-4020">

      <Card className={`h-full flex flex-col p-6 border-2 transition-all relative overflow-hidden ${isDisabled ? 'opacity-50 cursor-not-allowed border-slate-200' : 'cursor-pointer hover:border-violet-300 hover:shadow-xl hover:shadow-violet-100 border-slate-200'}`} onClick={!isDisabled ? onClick : undefined} data-id="element-4021">

        {/* Decorative blob */}
        {!isDisabled && <div className="absolute -right-8 -top-8 w-32 h-32 bg-violet-50 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-4022" />}

        {/* Beta Badge */}
        {mode.isBeta && <div className="absolute top-4 right-4" data-id="element-4023">
            <Badge variant="warning" size="sm" data-id="element-4024">
              Beta
            </Badge>
          </div>}

        <div className="relative z-10 flex flex-col h-full" data-id="element-4025">
          {/* Icon */}
          <div className={`p-4 rounded-2xl ${colors.bg} w-fit mb-4 shadow-sm border ${colors.border}`} data-id="element-4026">

            <Icon className={`w-8 h-8 ${colors.text}`} strokeWidth={2.5} data-id="element-4027" />
          </div>

          {/* Title & Subtitle */}
          <div className="mb-3" data-id="element-4028">
            <h3 className="text-xl font-black text-slate-900 mb-1 tracking-tight" data-id="element-4029">
              {mode.title}
            </h3>
            <p className="text-sm font-bold text-violet-600" data-id="element-4030">{mode.subtitle}</p>
          </div>

          {/* Description */}
          <p className="text-sm text-slate-600 font-medium leading-relaxed mb-4 flex-1" data-id="element-4031">
            {mode.description}
          </p>

          {/* Metadata */}
          <div className="space-y-3 mb-4" data-id="element-4032">
            {/* Time & Difficulty */}
            <div className="flex items-center gap-2 flex-wrap" data-id="element-4033">
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-200" data-id="element-4034">
                <Clock className="w-3.5 h-3.5" data-id="element-4035" />
                <span data-id="element-4036">{mode.estimatedMinutes} min</span>
              </div>
              <Badge variant="neutral" size="sm" data-id="element-4037">
                {mode.difficultyBand}
              </Badge>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-1.5" data-id="element-4038">
              {mode.tags.map(tag => <span key={tag} className="text-[10px] font-bold uppercase tracking-wider text-violet-600 bg-violet-50 px-2 py-0.5 rounded-md border border-violet-100" data-id="element-4039">

                  {tag}
                </span>)}
            </div>
          </div>

          {/* Action Button */}
          <Button variant={isDisabled ? 'secondary' : 'primary'} size="md" fullWidth disabled={isDisabled} className={!isDisabled ? 'bg-violet-600 hover:bg-violet-500 shadow-[0_4px_0_0_rgb(109,40,217)] hover:shadow-[0_4px_0_0_rgb(109,40,217)]' : ''} data-id="element-4040">

            {isDisabled ? 'Binnenkort' : 'Start'}
          </Button>
        </div>
      </Card>
    </motion.div>;
}